import { useEffect, useState, useMemo, CSSProperties } from 'react'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { getBreakpoints } from '@/utils'
import { Card } from '@/components/Cards'
import { IPlaylistScript } from '@/types/cinematics'
import { IPlaylistItem } from '@/types'
import { IBreakpoints, IPlaylistResponse } from '@/types/playlist'
import { SwiperOptions } from 'swiper/types'
import { Header } from '../PlaylistHeader'

import { PLAYLIST_TYPES } from '@/constants'
import classNames from 'classnames'
import classes from './styles.module.scss'
import 'swiper/css'

interface CarouselProps {
  fetchNextPage?: () => Promise<any>
  hasNextPage: boolean
  isFetching: boolean
  isPlaceholder?: boolean
  items: IPlaylistItem[]
  overflowStyle?: 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto'
  playlistType: typeof PLAYLIST_TYPES[keyof typeof PLAYLIST_TYPES]
  playlistData: IPlaylistResponse
  playlistOptions: IPlaylistScript['options']
  playlistScript: Pick<IPlaylistScript, '_id'>
  // Opt-in top-to-bottom gradient over the `.events` box, using this as the
  // top stop. Omit to keep the plain flat background every other caller gets.
  gradientColor?: string
}

interface SliderConfig {
  options: IPlaylistScript['options']
  breakpoints: IBreakpoints
  isMobile?: boolean
}

interface SliderOptions extends SwiperOptions {
  slidesPerView: number
}

const getSliderConfig: (args: SliderConfig) => SliderOptions = ({
  options,
  breakpoints,
}) => {
  return {
    allowTouchMove: options?.allowTouchMove !== false,
    autoplay: options?.autoplay || false,
    breakpoints: breakpoints,
    centeredSlides: options?.centeredSlides || false,
    effect: options?.effect || 'slide',
    initialSlide: 0,
    lazyPreloadPrevNext: 1,
    longSwipes: options?.longSwipes !== false,
    loop: options?.loop,
    rewind: options?.rewind,
    setWrapperSize: false,
    shortSwipes: options?.shortSwipes !== false,
    slidesPerView: 4,
    spaceBetween: options?.spaceBetween || 24,
    speed: options?.speed || 600,
    coverflowEffect: options?.coverflowEffect || {},
    grabCursor: false,
    preventClicks: true,
    preventInteractionOnTransition: true,
  }
}

export const PlaylistCarousel = (props: CarouselProps) => {
  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isPlaceholder,
    items,
    overflowStyle = 'visible',
    playlistData,
    playlistOptions,
    playlistType,
    gradientColor,
  } = props
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const breakpoints = useMemo(() => getBreakpoints(playlistOptions.columns), [playlistOptions.columns])
  const sliderConfig = useMemo(() => getSliderConfig({
    options: playlistOptions,
    breakpoints
  }), [playlistOptions, breakpoints])

  useEffect(() => {
    return () => {
      if (swiperInstance) {
        swiperInstance.destroy(true, true)
      }
    }
  }, [swiperInstance])

  return (
    <>
      <div
        className={classNames({
          [classes.carousel]: true,
          [classes.events]: playlistType === PLAYLIST_TYPES.EVENTS,
          [classes.horizontal]: playlistType === PLAYLIST_TYPES.HORIZONTAL,
          [classes.gradient]: !!gradientColor,
        })}
        style={gradientColor ? { '--playlist-gradient-color': gradientColor } as CSSProperties : undefined}
      >
        <Header
          options={playlistOptions}
          data={playlistData}
          totalItems={items?.length || 0}
          isPlaceholder={!!isPlaceholder}
          swiperInstance={swiperInstance}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
        />
        <Swiper
          style={{ overflow: overflowStyle }}
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          {...sliderConfig}
        >
          {items?.map((item, index) => (
            <SwiperSlide key={`item._id-${index}`}>
              <Card
                isPlaceholder={isPlaceholder}
                item={item}
                itemIndex={index}
                options={playlistOptions}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  )
}